import { useState, type ReactNode } from 'react'
import './DonutChart.css'

/**
 * A donut of parts of one whole, with its legend beside it — 29 Oct 2026.
 *
 * Asked for by the owner for the languages and countries card. Slices take
 * the categorical hues in a fixed order (validated: `dataviz` palette, light
 * surface), "Other" is grey, and a 2px surface gap separates the slices. Every
 * slice is named in the legend with its count and share, so colour is never
 * the only way to read it — three of the hues are below 3:1 on white.
 * Hovering a slice or a legend row shows the same figures.
 */
export const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948']
export const OTHER = '#9aa1ab'

export interface Slice {
  key: string
  label: string
  value: number
  color: string
  /** A flag or icon before the label. */
  icon?: ReactNode
}

const R = 70
const INNER = 44
const pct = (v: number, total: number) => (total > 0 ? `${((v / total) * 100).toFixed(1)} %` : '')

function arc(start: number, end: number): string {
  // Angles in turns from 12 o'clock; a full ring is drawn as two halves.
  const pt = (t: number, r: number) => [Math.sin(t * 2 * Math.PI) * r, -Math.cos(t * 2 * Math.PI) * r]
  const large = end - start > 0.5 ? 1 : 0
  const [x0, y0] = pt(start, R)
  const [x1, y1] = pt(end, R)
  const [x2, y2] = pt(end, INNER)
  const [x3, y3] = pt(start, INNER)
  return `M${x0},${y0}A${R},${R} 0 ${large} 1 ${x1},${y1}L${x2},${y2}A${INNER},${INNER} 0 ${large} 0 ${x3},${y3}Z`
}

export function DonutChart({ slices, title }: { slices: Slice[]; title: string }) {
  const [hover, setHover] = useState<string | null>(null)
  const total = slices.reduce((n, s) => n + s.value, 0)
  let at = 0
  const drawn = slices
    .filter((s) => s.value > 0)
    .map((s) => {
      const start = at
      at += s.value / total
      // A single slice is the whole ring; split it so the arc can be drawn.
      return { s, d: at - start >= 0.9999 ? `${arc(0, 0.5)}${arc(0.5, 0.99999)}` : arc(start, at) }
    })
  const shown = slices.find((s) => s.key === hover)

  return (
    <figure className="donut" aria-label={title}>
      <figcaption className="donut__title">{title}</figcaption>
      <div className="donut__body">
        <svg viewBox="-75 -75 150 150" className="donut__svg" role="img" aria-label={title}>
          {drawn.map(({ s, d }) => (
            <path
              key={s.key}
              d={d}
              fill={s.color}
              stroke="var(--color-bg, #fff)"
              strokeWidth={2}
              opacity={hover && hover !== s.key ? 0.45 : 1}
              onMouseEnter={() => setHover(s.key)}
              onMouseLeave={() => setHover(null)}
            >
              <title>{`${s.label}: ${s.value.toLocaleString('nb-NO')} (${pct(s.value, total)})`}</title>
            </path>
          ))}
          <text className="donut__centre" textAnchor="middle" y={-2}>
            {shown ? pct(shown.value, total) : total.toLocaleString('nb-NO')}
          </text>
          <text className="donut__centresub" textAnchor="middle" y={14}>
            {shown ? shown.label : 'in all'}
          </text>
        </svg>
        <ul className="donut__legend">
          {slices.map((s) => (
            <li
              key={s.key}
              className={hover === s.key ? 'donut__row donut__row--on' : 'donut__row'}
              onMouseEnter={() => setHover(s.key)}
              onMouseLeave={() => setHover(null)}
            >
              <span className="donut__swatch" style={{ background: s.color }} />
              {/* Always a cell, so a row without an icon keeps its columns. */}
              <span className="donut__icon">{s.icon}</span>
              <span className="donut__label">{s.label}</span>
              <span className="donut__n">{s.value.toLocaleString('nb-NO')}</span>
              <span className="donut__pct">{pct(s.value, total)}</span>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  )
}
